import React, { useState } from 'react';
import './JsonTreeEditor.css';

const TYPE_OPTIONS = ['string', 'number', 'boolean', 'object', 'array', 'null'];

function defaultValueForType(type) {
    switch (type) {
        case 'number': return 0;
        case 'boolean': return false;
        case 'object': return {};
        case 'array': return [];
        case 'null': return null;
        default: return '';
    }
}

function valueType(value) {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
}

// Inline form used to append a new key (object) or item (array).
function AddEntryForm({ isArray, onAdd, onCancel }) {
    const [keyName, setKeyName] = useState('');
    const [type, setType] = useState('string');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isArray && !keyName.trim()) return;
        onAdd(keyName.trim(), defaultValueForType(type));
        setKeyName('');
    };

    return (
        <form className="jte-add-form" onSubmit={handleSubmit}>
            {!isArray && (
                <input
                    className="jte-input"
                    placeholder="key name"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    autoFocus
                />
            )}
            <select className="jte-select" value={type} onChange={(e) => setType(e.target.value)}>
                {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button type="submit" className="jte-btn jte-btn-add">Add</button>
            <button type="button" className="jte-btn jte-btn-cancel" onClick={onCancel}>Cancel</button>
        </form>
    );
}

function PrimitiveEditor({ value, onChange }) {
    const type = valueType(value);

    if (type === 'boolean') {
        return (
            <input
                type="checkbox"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
            />
        );
    }
    if (type === 'number') {
        return (
            <input
                type="number"
                className="jte-input jte-value-input"
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
            />
        );
    }
    if (type === 'null') {
        return <span className="jte-null">null</span>;
    }
    // string
    return (
        <textarea
            className="jte-input jte-value-input jte-textarea"
            value={value}
            rows={value && value.length > 60 ? 3 : 1}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}

export default function JsonTreeEditor({ nodeKey, value, onChange, onRemove, depth = 0 }) {
    const [isOpen, setIsOpen] = useState(depth < 1);
    const [isAdding, setIsAdding] = useState(false);
    const type = valueType(value);
    const isContainer = type === 'object' || type === 'array';

    const handleChildChange = (childKey, childValue) => {
        if (type === 'array') {
            const next = value.slice();
            next[childKey] = childValue;
            onChange(next);
        } else {
            onChange({ ...value, [childKey]: childValue });
        }
    };

    const handleChildRemove = (childKey) => {
        if (type === 'array') {
            const next = value.slice();
            next.splice(childKey, 1);
            onChange(next);
        } else {
            const next = { ...value };
            delete next[childKey];
            onChange(next);
        }
    };

    const handleAdd = (newKey, newValue) => {
        if (type === 'array') {
            onChange([...value, newValue]);
        } else {
            onChange({ ...value, [newKey]: newValue });
        }
        setIsAdding(false);
    };

    const entries = type === 'array'
        ? value.map((v, i) => [i, v])
        : type === 'object'
            ? Object.entries(value)
            : [];

    return (
        <div className="jte-node" style={{ marginLeft: depth === 0 ? 0 : 16 }}>
            <div className="jte-row">
                {isContainer && (
                    <button
                        type="button"
                        className="jte-toggle"
                        onClick={() => setIsOpen((o) => !o)}
                    >
                        {isOpen ? '▼' : '▶'}
                    </button>
                )}
                {nodeKey !== undefined && (
                    <span className="jte-key">{nodeKey}</span>
                )}
                {isContainer ? (
                    <span className="jte-type-badge">
                        {type === 'array' ? `[${value.length}]` : `{${entries.length}}`}
                    </span>
                ) : (
                    <PrimitiveEditor value={value} onChange={onChange} />
                )}
                {onRemove && (
                    <button
                        type="button"
                        className="jte-btn jte-btn-remove"
                        title="Remove"
                        onClick={() => {
                            if (window.confirm('Remove this field? This cannot be undone after saving.')) {
                                onRemove();
                            }
                        }}
                    >
                        ×
                    </button>
                )}
            </div>

            {isContainer && isOpen && (
                <div className="jte-children">
                    {entries.map(([childKey, childValue]) => (
                        <JsonTreeEditor
                            key={childKey}
                            nodeKey={childKey}
                            value={childValue}
                            depth={depth + 1}
                            onChange={(v) => handleChildChange(childKey, v)}
                            onRemove={() => handleChildRemove(childKey)}
                        />
                    ))}

                    {isAdding ? (
                        <AddEntryForm
                            isArray={type === 'array'}
                            onAdd={handleAdd}
                            onCancel={() => setIsAdding(false)}
                        />
                    ) : (
                        <button
                            type="button"
                            className="jte-btn jte-btn-add-entry"
                            onClick={() => setIsAdding(true)}
                        >
                            + Add {type === 'array' ? 'item' : 'key'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
